import { ART_HOST, CARD_EDITIONS } from "@game/card-editions.js";
import { CARDS } from "@game/cards.js";
import {
  ANTE_EXTRA,
  DEFAULT_CURVE,
  FAMILY_BEATS,
  HAND_SIZE,
  LEVELS,
  RARITIES,
} from "@game/engine.js";
import { TIMING } from "@game/table.js";
import { ART_WIDTHS, artAt } from "@/lib/art-sizes";

/**
 * The game as anyone outside it reads it: `GET /api/cards/`, `/api/cards/<id>/`
 * and `/api/rules/` for deck tools, `/cards/` and `/cards/<id>/` for people.
 * The game's own wire format (`/api/game/cards`) keeps its one-letter keys;
 * this spells them out and adds what the signed catalog prints of each card.
 */
export interface PublicImage {
  original: string;
  /** Web copies by width in pixels (`lib/art-sizes.ts`). */
  sizes: Record<string, string>;
}

export interface PublicEdition {
  /** Base, Foil or Serialized. */
  finish: string;
  /** The name printed on this edition. */
  name: string;
  catalogId: string;
  /** The edition id a minted card carries on chain. */
  onchainEditionId: number;
  maxSupply: number;
  /** Null while the catalog holds only a generic stand-in for this face. */
  image: PublicImage | null;
}

export interface PublicCard {
  id: string;
  /** The name printed on the card. */
  name: string;
  /** The family: CLUB, MUTANT, COSMIC or ARCANA. */
  type: string;
  set: string;
  rarity: string;
  /** I (Common) to V (Legendary): a card upgrades onto a lower level. */
  level: string;
  /** The number on each edge, as the board compares them. */
  stats: { top: number; right: number; bottom: number; left: number };
  /** The Base edition's face. */
  image: PublicImage;
  editions: PublicEdition[];
  url: `/cards/${string}/`;
}

type GameCard = (typeof CARDS)[number];

function imageOf(cid: string): PublicImage {
  const original = `${ART_HOST}/art/${cid}`;
  return {
    original,
    sizes: Object.fromEntries(
      ART_WIDTHS.map((width) => [width, artAt(original, width) ?? original])
    ),
  };
}

const editionsById = new Map(Object.entries(CARD_EDITIONS));
const levelOf = new Map(Object.entries(LEVELS));

function publicCard(card: GameCard): PublicCard {
  const editions = (editionsById.get(card.id) ?? []).map(
    ({ cid, ...edition }) => ({
      ...edition,
      image: cid ? imageOf(cid) : null,
    })
  );
  // The Base edition names the card and gives its face: the signed catalog
  // is the authority, and the test holds the game's own list to it.
  const base = (editionsById.get(card.id) ?? [])[0];
  if (!base?.cid) throw new Error(`Card ${card.id} has no Base face`);
  return {
    id: card.id,
    name: base.name,
    type: card.f,
    set: card.tr,
    rarity: card.r,
    level: levelOf.get(card.r) ?? "",
    stats: { top: card.T, right: card.R, bottom: card.B, left: card.L },
    image: imageOf(base.cid),
    editions,
    url: `/cards/${card.id}/`,
  };
}

export const PUBLIC_CARDS: readonly PublicCard[] = CARDS.map(publicCard);
const byId = new Map(PUBLIC_CARDS.map((card) => [card.id, card]));

/**
 * The face a card shows in one finish. A finish whose art the catalog has not
 * revealed (most Serialized editions) shows the Base face, and says so, rather
 * than the catalog's generic stand-in.
 */
export function faceOf(card: PublicCard, finish: string) {
  const edition = card.editions.find((entry) => entry.finish === finish);
  return {
    edition,
    image: edition?.image ?? card.image,
    revealed: Boolean(edition?.image),
  };
}

/** A card by its three-digit id, or nothing for anything else. */
export function publicCardById(id: string): PublicCard | undefined {
  return byId.get(id);
}

/** The rules a deck tool needs, from the constants the game itself plays by. */
export const PUBLIC_RULES = {
  hand: {
    size: HAND_SIZE,
    /** How many of each rarity a hand is dealt. */
    rarityCurve: Object.fromEntries(
      RARITIES.map((rarity, index) => [rarity, DEFAULT_CURVE[index] ?? 0])
    ),
    /** With the ante on, one more card is picked and one pulled at random. */
    anteExtra: ANTE_EXTRA,
  },
  rarities: RARITIES.map((rarity) => ({
    rarity,
    level: levelOf.get(rarity) ?? "",
  })),
  /** Each type beats the one it names; it only breaks a tie on a touching edge. */
  typeBeats: FAMILY_BEATS,
  board: { rows: 3, columns: 3 },
  turnSeconds: TIMING.turnMs / 1000,
  rulesUrl: "/rules/",
} as const;
